/**
 * Forging a weapon (ตีบวก): raising it +1 … +15 for more damage, and tempering it with one of five
 * elements (ตีธาตุ) up to level 8. Each step costs ตำลึง and may fail; past +7 a failed hammer
 * knocks the weapon back a level. A forged weapon shows it: lightning from +7 (blue), +10 (red) and
 * +15 (black), and its element circling the blade once tempered to the full 8.
 */
import type { EquipSlot, ItemStats } from './items';

/** The forging elements. Not the martial arts' elements (data/elements): these temper steel. */
export type ForgeElement = 'poison' | 'fire' | 'earth' | 'water' | 'wind';
export const FORGE_ELEMENTS: readonly ForgeElement[] = ['poison', 'fire', 'earth', 'water', 'wind'];

export const FORGE_ELEMENT_INFO: Readonly<
  Record<ForgeElement, { name: string; desc: string; color: string }>
> = {
  poison: { name: 'พิษ', desc: 'พิษซึมลึกถึงจุดตาย คริติคอลสูงขึ้น', color: '#9a5ad8' },
  fire: { name: 'ไฟ', desc: 'เพลิงแผดเผาในใบอาวุธ พลังโจมตีสูงขึ้น', color: '#e8602a' },
  earth: { name: 'ดิน', desc: 'หนักแน่นดุจภูผา ป้องกันและพลังชีวิตสูงขึ้น', color: '#a8824a' },
  water: { name: 'น้ำ', desc: 'ไหลเวียนดุจสายธาร ดูดเลือดคืนจากทุกแผล', color: '#3a8ac8' },
  wind: { name: 'ลม', desc: 'เบาดุจสายลม เคลื่อนที่เร็วขึ้น', color: '#8ac8a8' },
};

/** Highest +, and highest element level. */
export const FORGE_MAX = 15;
export const INFUSE_MAX = 8;

/** How a weapon has been forged: its +, and its element and that element's level. */
export interface WeaponForge {
  readonly plus: number;
  readonly el: ForgeElement | null;
  readonly lv: number;
}
export const UNFORGED: WeaponForge = { plus: 0, el: null, lv: 0 };

/** Chance that the hammer takes a weapon to +n (index n − 1). */
const PLUS_CHANCE = [1, 1, 1, 1, 1, 0.85, 0.75, 0.65, 0.55, 0.45, 0.4, 0.35, 0.3, 0.25, 0.2];
/** Chance that tempering takes an element to level n (index n − 1). */
const INFUSE_CHANCE = [1, 1, 1, 0.8, 0.7, 0.6, 0.5, 0.4];

/** From this + up, a failed hammer knocks the weapon back one level. */
export const FORGE_SLIP_FROM = 7;

/** What it costs, and the chance, to raise a weapon at `plus` to the next +. */
export function forgeStep(plus: number): { cost: number; chance: number } | null {
  if (plus >= FORGE_MAX) return null;
  const n = plus + 1;
  return { cost: 30 * n + 5 * n * n, chance: PLUS_CHANCE[plus]! };
}

/** What it costs, and the chance, to take an element at `lv` to the next level. */
export function infuseStep(lv: number): { cost: number; chance: number } | null {
  if (lv >= INFUSE_MAX) return null;
  const n = lv + 1;
  return { cost: 40 * n + 6 * n * n, chance: INFUSE_CHANCE[lv]! };
}

/** The lightning a +level calls up, or none below +7. */
export type ForgeTier = 'blue' | 'red' | 'black';
export function forgeTier(plus: number): ForgeTier | null {
  return plus >= 15 ? 'black' : plus >= 10 ? 'red' : plus >= 7 ? 'blue' : null;
}

/** Each + adds 6% of the weapon's own damage and one point more. */
export function plusAtk(baseAtk: number, plus: number): number {
  return Math.round((baseAtk * 0.06 + 1) * plus * 10) / 10;
}

/** What an element at `lv` adds. */
export function infuseStats(el: ForgeElement, lv: number, baseAtk: number): ItemStats {
  switch (el) {
    case 'fire':
      return { atk: Math.round(baseAtk * 0.04 * lv * 10) / 10 + lv };
    case 'poison':
      return { crit: Math.round(lv * 0.01 * 1000) / 1000 };
    case 'earth':
      return { def: lv * 2, maxHp: lv * 12 };
    case 'water':
      return { lifesteal: Math.round(lv * 0.006 * 1000) / 1000 };
    case 'wind':
      return { speed: Math.round(lv * 0.01 * 1000) / 1000 };
  }
}

/** Items v2 (docs/systems/ITEMS_V2.md §2): the slots ตีบวก / ตีธาตุ take; armour and accessories as well as weapons. */
export const FORGE_SLOTS: readonly EquipSlot[] = ['weapon', 'head', 'body', 'legs', 'feet', 'ring', 'pendant'];

/** What + adds to an armour piece or an accessory: 6 % of its own defence and life per +, and a little more. */
export function armourPlusStats(base: ItemStats, plus: number): ItemStats {
  if (!plus) return {};
  return {
    def: Math.round(((base.def ?? 0) * 0.06 + 1) * plus * 10) / 10,
    maxHp: Math.round(((base.maxHp ?? 0) * 0.06 + 5) * plus),
  };
}

/** Everything forging adds to a weapon whose own damage is `baseAtk`. */
export function forgeStats(f: WeaponForge, baseAtk: number): ItemStats {
  const s: ItemStats = f.el && f.lv ? { ...infuseStats(f.el, f.lv, baseAtk) } : {};
  if (f.plus) s.atk = Math.round(((s.atk ?? 0) + plusAtk(baseAtk, f.plus)) * 10) / 10;
  return s;
}

export const isForgeElement = (v: unknown): v is ForgeElement =>
  typeof v === 'string' && (FORGE_ELEMENTS as readonly string[]).includes(v);
